import FJAdvertisingItem from '../../FJCommonComponent/FJAdvertisingItem/FJAdvertisingItem';
import PropTypes from 'prop-types';
import styles from './FJAdvertising.module.less';
import classNames from 'classnames';
import FJArrowIcon from '../../../../assets/svg/community/more.svg?react';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import useIsInViewport from '../../../../hooks/useIsInViewPort';

const ITEM_WIDTH_AND_SPACE = 480 + 12;
const CAROUSEL_INTERVAL = 5000; // 自动滚动间隔（毫秒）
const RESIZE_DEBOUNCE = 150; // resize防抖时间（毫秒）
const INIT_UPDATE_DELAY = 100; // 初始更新延迟（毫秒）

const FJAdvertising = ({ list }) => {
    const itemRef = useRef([]);
    const [virtualIdx, setVirtualIdx] = useState(0); // 虚拟索引，初始为1（因为开头有一个副本）
    const [isOverflowing, setIsOverflowing] = useState(false); // 是否超出屏幕
    const carouselWrapperRef = useRef(null);
    const autoScrollTimerRef = useRef(null);
    const resizeTimerRef = useRef(null);
    const isPausedRef = useRef(false);
    const listLength = useMemo(() => list?.length || 0, [list]);
    const [containerRef, isVisible] = useIsInViewport();
    useEffect(() => {
        if (isVisible) {
            startAutoScroll();
        } else {
            stopAutoScroll();
        }
    }, [isVisible]);

    /**
     * 检查内容是否超出屏幕（使用ref避免依赖变化）
     */
    const checkOverflow = useCallback(() => {
        const currentListLength = listLength;
        if (!containerRef.current || !carouselWrapperRef.current || currentListLength === 0) {
            setIsOverflowing(false);
            return;
        }

        const containerWidth = containerRef.current.offsetWidth;
        const totalContentWidth = ITEM_WIDTH_AND_SPACE * currentListLength;
        const isOverflow = totalContentWidth > containerWidth;
        setIsOverflowing(isOverflow);
    }, []); // 移除listLength依赖，使用ref

    /**
     * 停止自动滚动
     */
    const stopAutoScroll = useCallback(() => {
        if (autoScrollTimerRef.current) {
            clearInterval(autoScrollTimerRef.current);
            autoScrollTimerRef.current = null;
        }
    }, []);
    // 移动左边第二个
    const updateCarouselItemStyle = useCallback(
        virtualIdx => {
            const list = itemRef.current;
            if (!list) return;
            let vIdx = virtualIdx % listLength;
            if (virtualIdx > 0 && vIdx === 0) {
                vIdx = listLength;
            }
            const double = Math.floor(Math.abs(virtualIdx > 0 ? virtualIdx - 1 : virtualIdx) / listLength);
            const listLengthPixel = ITEM_WIDTH_AND_SPACE * listLength;
            for (let i = 0; i < list.length; i++) {
                const item = list[i];
                if (!item) continue;
                let translateX = 0;
                if (isOverflowing) {
                    if (vIdx > i + 1 && vIdx > 0) {
                        translateX = listLengthPixel * (double + 1);
                    } else if (i + 1 >= listLength + vIdx && vIdx <= 0) {
                        translateX = -listLengthPixel * (double + 1);
                    } else if (vIdx > 0) {
                        translateX = listLengthPixel * double;
                    } else if (vIdx <= 0) {
                        translateX = -listLengthPixel * double;
                    }
                }

                item.style.transform = `translateX(${translateX}px)`;
            }
        },
        [listLength, isOverflowing],
    );
    // 移动左边第一个
    // const updateCarouselItemStyle = useCallback(
    //     virtualIdx => {
    //         const list = itemRef.current;
    //         if (!list) return;
    //         let vIdx = virtualIdx % listLength;

    //         const double = Math.floor(Math.abs(virtualIdx) / listLength);
    //         const listLengthPixel = ITEM_WIDTH_AND_SPACE * listLength;
    //         for (let i = 0; i < list.length; i++) {
    //             const item = list[i];
    //             if (!item) continue;
    //             let translateX = 0;
    //             if (isOverflowing) {
    //                 if (vIdx > i && vIdx >= 0) {
    //                     translateX = listLengthPixel * (double + 1);
    //                 } else if (i + 1 > listLength + vIdx && vIdx < 0) {
    //                     translateX = -listLengthPixel * (double + 1);
    //                 } else if (virtualIdx >= 0) {
    //                     translateX = listLengthPixel * double;
    //                 } else if (virtualIdx < 0) {
    //                     translateX = -listLengthPixel * double;
    //                 }
    //             }

    //             item.style.transform = `translateX(${translateX}px)`;
    //         }
    //     },
    //     [listLength, isOverflowing],
    // );

    /**
     * 处理过渡动画结束
     */
    const handleTransitionEnd = useCallback(
        e => {
            if (e.propertyName !== 'transform') return;
            updateCarouselItemStyle(virtualIdx);
        },
        [virtualIdx, updateCarouselItemStyle],
    );

    /**
     * 启动自动滚动
     */
    const startAutoScroll = useCallback(() => {
        stopAutoScroll();
        const currentListLength = listLength;
        if (isOverflowing && currentListLength > 0 && !isPausedRef.current) {
            autoScrollTimerRef.current = setInterval(() => {
                setVirtualIdx(prev => prev + 1);
            }, CAROUSEL_INTERVAL);
        }
    }, [isOverflowing, stopAutoScroll, listLength]);

    /**
     * 处理鼠标进入
     */
    const handleMouseEnter = useCallback(() => {
        isPausedRef.current = true;
        stopAutoScroll();
    }, [stopAutoScroll]);

    /**
     * 处理鼠标离开
     */
    const handleMouseLeave = useCallback(() => {
        isPausedRef.current = false;
        if (isOverflowing) {
            startAutoScroll();
        }
    }, [startAutoScroll, isOverflowing]);

    // ==================== Effects ====================
    /**
     * 初始化：检查溢出状态和设置resize监听
     * 优化：使用ref存储checkOverflow，避免依赖变化
     */
    const checkOverflowRef = useRef(checkOverflow);
    checkOverflowRef.current = checkOverflow;

    useEffect(() => {
        if (listLength === 0) return;
        updateCarouselItemStyle(0);

        const timer = setTimeout(() => {
            checkOverflowRef.current();
        }, INIT_UPDATE_DELAY);
        const handleResize = () => {
            if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
            resizeTimerRef.current = setTimeout(() => {
                checkOverflowRef.current();
                setVirtualIdx(0);
                updateCarouselItemStyle(0);
            }, RESIZE_DEBOUNCE);
        };

        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
            clearTimeout(timer);
            if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
            stopAutoScroll();
            window.removeEventListener('resize', handleResize);
        };
    }, [listLength, stopAutoScroll, updateCarouselItemStyle]);

    /**
     * 当溢出状态变化时，启动或停止自动滚动
     * 优化：使用ref存储函数，减少依赖
     */
    const startAutoScrollRef = useRef(startAutoScroll);
    startAutoScrollRef.current = startAutoScroll;

    useEffect(() => {
        if (isOverflowing && !isPausedRef.current) {
            startAutoScrollRef.current();
        } else {
            stopAutoScroll();
        }
        return () => {
            stopAutoScroll();
        };
    }, [isOverflowing, stopAutoScroll]);

    /**
     * 轮播容器样式（优化：使用CSS变量减少字符串拼接）
     */
    const carouselStyle = useMemo(() => {
        const translateX = ITEM_WIDTH_AND_SPACE * virtualIdx;
        return {
            transform: `translateX(${-translateX}px)`,
            transition: 'transform 0.3s ease-in-out', // 添加过渡动画
        };
    }, [virtualIdx]);

    /**
     * 处理上一张/下一张按钮点击
     */
    const handlePrev = useCallback(() => {
        const currentListLength = listLength;
        if (!isOverflowing || currentListLength <= 1) return;
        stopAutoScroll();
        updateCarouselItemStyle(virtualIdx);
        setVirtualIdx(prev => prev - 1);
        setTimeout(() => {
            if (!isPausedRef.current && isOverflowing) {
                startAutoScroll();
            }
        }, 0);
    }, [isOverflowing, stopAutoScroll, startAutoScroll, listLength, virtualIdx, updateCarouselItemStyle]);

    const handleNext = useCallback(() => {
        const currentListLength = listLength;
        if (!isOverflowing || currentListLength <= 1) return;
        stopAutoScroll();
        updateCarouselItemStyle(virtualIdx);
        setVirtualIdx(prev => prev + 1);
        setTimeout(() => {
            if (!isPausedRef.current && isOverflowing) {
                startAutoScroll();
            }
        }, 0);
    }, [isOverflowing, stopAutoScroll, startAutoScroll, listLength, virtualIdx, updateCarouselItemStyle]);

    if (listLength === 0) return null;

    return (
        <div ref={containerRef} className={styles.advertising_container}>
            {isOverflowing && (
                <>
                    <div className={classNames(styles.nav_button, styles.nav_button_left)} onClick={handlePrev}>
                        <FJArrowIcon className={styles.arrow_icon} />
                    </div>
                    <div className={classNames(styles.nav_button, styles.nav_button_right)} onClick={handleNext}>
                        <FJArrowIcon className={styles.arrow_icon} />
                    </div>
                </>
            )}
            <div
                className={styles.carousel_wrapper}
                style={carouselStyle}
                ref={carouselWrapperRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onTransitionEnd={handleTransitionEnd}
            >
                {list.map((item, idx) => (
                    <div
                        ref={el => {
                            if (el) {
                                itemRef.current[idx] = el;
                            }
                        }}
                        className={styles.carousel_item}
                        key={`ad-item-${idx}`}
                    >
                        <FJAdvertisingItem data={item} />
                    </div>
                ))}
            </div>
        </div>
    );
};

FJAdvertising.propTypes = {
    list: PropTypes.array.isRequired,
};

export default FJAdvertising;
